'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import styles from './UploadCard.module.css';
import { Button } from './Button';

export interface UploadCardTipProps {
  /** Icon/avatar for the tip (e.g., Sola mascot) */
  icon?: React.ReactNode;
  /** Header text above the tip */
  header?: React.ReactNode;
  /** Tip content text */
  content: string;
}

export interface UploadCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Main heading for the upload area */
  heading?: string;
  /** Description or instruction text */
  description?: React.ReactNode;
  /** Custom upload icon (defaults to cloud upload) */
  uploadIcon?: React.ReactNode;
  /** Background image URL */
  backgroundImage?: string;
  /** Tip panel content (structured) */
  tip?: UploadCardTipProps;
  /** Arbitrary JSX rendered in the tip panel area (overrides tip prop) */
  tipNode?: React.ReactNode;
  /** Handler for file drop */
  onFileDrop?: (files: FileList) => void;
  /** Handler for browse click */
  onBrowseClick?: () => void;
  /** Additional class names */
  className?: string;
  /** Children to render inside drop zone (overrides default content) */
  children?: React.ReactNode;
}

export const UploadCard = React.forwardRef<HTMLDivElement, UploadCardProps>(
  (
    {
      heading = 'Add new candidates',
      description,
      uploadIcon,
      backgroundImage = '/img/floats/background-blue-rounded.png',
      tip,
      tipNode,
      onFileDrop,
      onBrowseClick,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [isDragOver, setIsDragOver] = React.useState(false);

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (onFileDrop && e.dataTransfer.files.length > 0) {
        onFileDrop(e.dataTransfer.files);
      }
    };

    const defaultDescription = (
      <>
        Drag and drop CV files or
        <br />
        <Button variant="tertiary" size="sm" noPadding onClick={onBrowseClick}>
          browse files
        </Button>
      </>
    );

    return (
      <div className={styles.root}>
        <div
          ref={ref}
          className={clsx(styles.card, className)}
          style={{
            backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
          }}
          {...props}
        >
          {/* Tip Panel */}
          {tipNode && tipNode}
          {!tipNode && tip && (
            <div className={styles.tipPanel}>
              <div className={styles.tipHeader}>
                {tip.header && (
                  <span className={styles.tipHeaderText}>
                    {tip.icon && tip.icon}
                    {tip.header}
                  </span>
                )}
              </div>
              <p className={styles.tipContent}>{tip.content}</p>
            </div>
          )}

          {/* Drop Zone */}
          <div
            className={clsx(styles.dropZone, isDragOver && styles.dragOver)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {children || (
              <>
                <h2 className={styles.heading}>{heading}</h2>
                {uploadIcon || (
                  <img
                    src="/img/floats/cloud-upload.png"
                    alt="Upload"
                    width={40}
                    height={40}
                  />
                )}
                <p className={styles.description}>
                  {description || defaultDescription}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
);

UploadCard.displayName = 'UploadCard';

export default UploadCard;
